import type { FetchMatcher, MatcherFixture } from 'tio-data/demo/types';
import { DEMO_LOAN_BALANCE_AS_OF, USER_ID } from 'tio-employee/demo/identity-fixtures';
import { DEMO_DOCUMENTS } from 'tio-employee/demo/loan-documents-fixtures';
import { demoLatestLegacyRun } from 'tio-employee/demo/loan-estimator-fixtures';
import {
  DEMO_LOAN_ORIGINAL_DATE,
  DEMO_PSLF_LOAN_PLAN,
  DEMO_PSLF_QUALIFYING_PAYMENTS,
  demoLoanPlan,
  demoPslfActive,
} from 'tio-employee/demo/loan-profile';
import { demoPslfLatestSubmittedAt } from 'tio-employee/demo/products/pslf-fixtures';
import type { DemoFixtureIdentity, DemoScenario } from 'tio-employee/demo/types';
import {
  getDemoDebtLevel,
  isFirstLoginPersona,
  isPslfEligibleClient,
  type DemoSessionSelectionSnapshot,
} from 'tio-employee/utils/career-pathways-demo-data';

// Copied from the wellnessInfoRequest / enqueueWellnessRecalculationRequest request snapshots.
const WELLNESS_PROFILE_URL = `/users/${USER_ID}/user-profile?profile_key=UserProfileAttribute.WELLNESS_TRAIN`;
const WELLNESS_ENQUEUE_URL = `/users/${USER_ID}/queue-user-wellness-train`;

// tio-user-profile wellness_train_config.current_wellness_train_version.
const WELLNESS_TRAIN_VERSION = '2026.06.24.1';

export type WellnessCalc = 'non_profit_pslf' | 'non_profit_sf' | 'for_profit';

// The demo loan's NSLDS repayment-plan codes: SF 'Standard Repayment', IB 'Income-Based Repayment – Partial Financial Hardship'.
export type NsldsPlanCode = 'SF' | 'IB';

export type WellnessTrainCode =
  | 'D-2'
  | 'D-5'
  | 'N-3'
  | 'N-5'
  | 'N-10'
  | 'N-12'
  | 'N-13'
  | 'N-20'
  | 'N-22'
  | 'N-23'
  | 'N-24'
  | 'N-29';

type ComponentType =
  | 'add_account'
  | 'score'
  | 'dash_summary'
  | 'to_idr_tool'
  | 'transitional_idr_results'
  | 'to_coaching'
  | 'to_strategy_finder'
  | 'transitional_strategy_finder_results'
  | 'to_pslf_form_submission'
  | 'to_pslf_tool'
  | 'transitional_pslf_tool_results'
  | 'transitional_pslf_form_submission';

type ComponentName =
  | 'prompt_to_add_account'
  | 'score_need_attention'
  | 'summary_challenging'
  | 'recommend_idr_tool'
  | 'transition_idr_tool'
  | 'recommend_coaching_affordability'
  | 'score_on_track'
  | 'transition_strategy_finder'
  | 'summary_heard_pslf'
  | 'recommend_strategy_finder_short'
  | 'recommend_coaching_affordability_pslf'
  | 'score_need_adjustment'
  | 'summary_pslf_renew_idr'
  | 'summary_pslf_no_credit'
  | 'recommend_pslf_tool'
  | 'transition_pslf_tool'
  | 'transition_pslf_form'
  | 'recommend_pslf_form_submission';

interface WellnessButton {
  name: string;
  text: string;
}

interface ComponentContent {
  type: ComponentType;
  text: string;
  button: WellnessButton | null;
}

interface TrainDefinition {
  rating: number;
  description: string;
  components: ComponentName[];
}

export interface WellnessComponent {
  type: ComponentType;
  name: ComponentName;
  sub_title: string | null;
  text: string;
  button: WellnessButton | null;
}

// The flat snake_case profile value tio-user-profile's get_and_set_wellness_train stores and tio-api returns.
export interface WellnessTrainPayload {
  wellness_train: WellnessTrainCode;
  wellness_train_rating: number;
  wellness_train_description: string;
  components: WellnessComponent[];
  has_linked_loans: boolean;
  balance_updated_at: string | null;
  evidence_of_loans: boolean;
  obs_updated_at: string | null;
  calculation_updated_at: string;
  nslds: boolean;
  pslf_form_submitted_date: string | null;
  pslf_form_submitted: boolean;
  pslf_submitted_within_11m: boolean;
  pslf_submitted_within_4m: boolean;
  been_paying_for_years: string;
  has_used_calculator: boolean;
  calculator_last_use_record_id: string | null;
  calculator_last_use_date: string | null;
  latest_nslds_doc_id: string | null;
  latest_loan_statement_doc_id: string | null;
  nslds_as_of?: string;
  nslds_processed_as_of?: string;
  has_ffel_loans?: boolean;
  has_direct_loans?: boolean;
  has_perkins_loans?: boolean;
  has_parentplus_loans?: boolean;
  in_grace_period_or_deferment?: boolean;
  non_optimal_repayment_plan?: boolean;
  idr_repayment_plan?: boolean;
  pslf_count_shown?: boolean;
  pslf_count?: number;
  idr_anniversary_date?: string | null;
  balance_going_down?: boolean;
  has_recent_forbearance?: boolean;
  has_default_history?: boolean;
  currently_in_default?: boolean;
  currently_in_forbearance?: boolean;
  first_loan_date?: string;
  last_loan_date?: string;
  first_loan_time?: string;
  last_loan_time?: string;
  file_type?: 'nslds' | 'statement';
  calc: WellnessCalc | null;
  past_due_payments?: boolean;
  has_federal_loans?: boolean;
  grace_or_forbearance?: boolean;
  version: string;
  statement_balance_updated_at: string | null;
  statement_obs_updated_at: string | null;
  first_time_on_this_train: string | null;
}

const COMPONENT_SUBTITLES: Partial<Record<ComponentType, string>> = {
  to_idr_tool: 'Use Our IDR Tool',
  transitional_idr_results: 'Use Our IDR Tool',
  to_coaching: 'Talk to a Student Loan Coach',
  to_strategy_finder: 'Use Our Strategy Finder',
  transitional_strategy_finder_results: 'Use Our Strategy Finder',
  to_pslf_form_submission: 'Submit Your PSLF Form',
  to_pslf_tool: 'Use Our PSLF Tool',
  transitional_pslf_tool_results: 'Use Our PSLF Tool',
  transitional_pslf_form_submission: 'Submit Your PSLF Form',
};

const COMPONENTS: Record<ComponentName, ComponentContent> = {
  prompt_to_add_account: {
    type: 'add_account',
    text: "Upload a MyStudentData file or a loan statement and we'll provide you with tailored recommendations.",
    button: { name: 'add_loan_account', text: 'Add Loan Account' },
  },
  score_need_attention: {
    type: 'score',
    text: 'Needs Attention',
    button: null,
  },
  summary_challenging: {
    type: 'dash_summary',
    text: 'Your student loans are {age_of_loans} old. Did you know it takes the average borrower 21 years to pay off their student loans? We know paying off student loans can be challenging. But we’re here to help make it easier.',
    button: null,
  },
  recommend_idr_tool: {
    type: 'to_idr_tool',
    text: 'Did you know income-driven repayment plans can help you afford your payments? Want to see your estimated payment? Go to the IDR tool, and answer a few short questions, it’ll take <5 minutes.',
    button: { name: 'idr_tool', text: 'Explore IDR' },
  },
  transition_idr_tool: {
    type: 'transitional_idr_results',
    text: 'Review your IDR results. If your situation has changed, you can complete the questions again to see if your recommendations change.',
    button: { name: 'most_recent_idr_result', text: 'Review Results' },
  },
  recommend_coaching_affordability: {
    type: 'to_coaching',
    text: 'Need help choosing the right option or still struggling with affordability? Reach out to our student loan coaches for help, our experts are always free of charge and never sell you anything.',
    button: { name: 'request_coaching', text: 'Request Coaching' },
  },
  score_on_track: {
    type: 'score',
    text: 'On Track',
    button: null,
  },
  transition_strategy_finder: {
    type: 'transitional_strategy_finder_results',
    text: 'Review your Strategy Finder results. If your situation has changed, you can complete the questions again to see if your recommendations change.',
    button: { name: 'most_recent_sf_result', text: 'Review Results' },
  },
  summary_heard_pslf: {
    type: 'dash_summary',
    text: "Your student loans are {age_of_loans} old. Have you heard of Public Service Loan Forgiveness (PSLF)? You could qualify for loan forgiveness after 10 years of income-driven payments while employed at a qualifying public service employer like yours. You can even get credit for past payments. Your current repayment plan doesn't qualify for PSLF, so you'll need to switch to an income-driven repayment plan to qualify.",
    button: null,
  },
  recommend_strategy_finder_short: {
    type: 'to_strategy_finder',
    text: 'Want to see your estimated payment and projected forgiveness? Go to the strategy finder tool, and answer a few short questions.',
    button: { name: 'strategy_finder', text: 'Find Strategy' },
  },
  recommend_coaching_affordability_pslf: {
    type: 'to_coaching',
    text: 'Need additional help or worried about affordability? Our student loan coaches are available via email, scheduled call, or screen share. We’ve coached thousands of borrowers on PSLF and can help answer your questions.',
    button: { name: 'request_coaching', text: 'Request Coaching' },
  },
  score_need_adjustment: {
    type: 'score',
    text: 'May Need Attention',
    button: null,
  },
  summary_pslf_renew_idr: {
    type: 'dash_summary',
    text: 'You’ve made {pslf_count} PSLF-qualifying payment(s), that means you only have {pslf_remaining_count}  months left! Have you gotten credit for ALL your qualifying employment history?\nIt’s always important to renew your IDR plan on time each year to avoid a lapse in your plan which can lead to much higher payments or stop progress toward PSLF.',
    button: null,
  },
  summary_pslf_no_credit: {
    type: 'dash_summary',
    text: 'Have you heard of Public Service Loan Forgiveness (PSLF)? You’re currently repaying your student loans under a qualifying plan and working for a qualifying employer, but your documents show you haven’t gotten any credit toward PSLF.',
    button: null,
  },
  recommend_pslf_tool: {
    type: 'to_pslf_tool',
    text: 'Want to see your estimated payment and projected forgiveness? Go to the PSLF Tool, and answer a few short questions',
    button: { name: 'pslf_tool', text: 'Explore PSLF' },
  },
  transition_pslf_tool: {
    type: 'transitional_pslf_tool_results',
    text: 'Review your PSLF results. If your situation has changed, you can complete the questions again to see if your recommendations change.',
    button: { name: 'most_recent_pslf_result', text: 'Review Results' },
  },
  transition_pslf_form: {
    type: 'transitional_pslf_form_submission',
    text: "You've completed your PSLF form via <a href='https://tuition.io'>tuition.io</a>. Check on the review status on <a href='https://studentaid.gov'>studentaid.gov</a> 60-90 days after submission for an updated qualifying payment count.",
    button: { name: 'https://studentaid.gov/pslf/', text: 'Form Status' },
  },
  recommend_pslf_form_submission: {
    type: 'to_pslf_form_submission',
    text: "You MUST submit a PSLF form for every unique qualifying employer in order to get credit for your work. You can submit as many PSLF forms as needed, and you can do this easily via <a href='https://tuition.io'>tuition.io</a> in <2 minutes! Use the ‘current employer flow’ to get credit for your time at your employer, and prior employment to get credit for any previous qualifying employment.",
    button: { name: 'pslf_form_submission', text: 'Submit Form' },
  },
};

// Only the trains buildWellnessTrain can reach carry copied text.
type DemoTrainCode = 'D-2' | 'D-5' | 'N-3' | 'N-10' | 'N-22' | 'N-23' | 'N-29';

const TRAINS: Record<DemoTrainCode, TrainDefinition> = {
  'D-2': {
    rating: 3,
    description: 'Any company type + wellness access + any/no doc upload + no loan accounts added',
    components: ['prompt_to_add_account'],
  },
  'D-5': {
    rating: 3,
    description: 'No wellness access',
    components: [],
  },
  'N-3': {
    rating: 2,
    description:
      'For Profit+ NSLDS + may be struggling with affordability + hx default/delinquency OR Graduated/extended repayment OR multiple forbearances OR current balance > starting balance',
    components: [
      'score_need_attention',
      'summary_challenging',
      'recommend_idr_tool',
      'transition_idr_tool',
      'recommend_coaching_affordability',
    ],
  },
  'N-10': {
    rating: 2,
    description: 'No PSLF + NSLDS + Wrong repayment plan + direct only, no PPLUS',
    components: [
      'score_need_attention',
      'summary_heard_pslf',
      'recommend_strategy_finder_short',
      'transition_strategy_finder',
      'recommend_coaching_affordability_pslf',
    ],
  },
  'N-22': {
    rating: 5,
    description:
      'PSLF + NSLDS + IDR + PSLF Qualifying Count + Submitted PSLF form <11 mo ago + direct only, no PPLUS',
    components: [
      'score_on_track',
      'summary_pslf_renew_idr',
      'recommend_pslf_tool',
      'transition_pslf_tool',
      'recommend_coaching_affordability_pslf',
    ],
  },
  'N-23': {
    rating: 3,
    description:
      'PSLF + NSLDS + IDR + NO PSLF Qualifying Count + No PSLF form or >11 months + direct only, no PPLUS',
    components: [
      'score_need_adjustment',
      'summary_pslf_no_credit',
      'recommend_pslf_tool',
      'transition_pslf_tool',
      'recommend_pslf_form_submission',
      'transition_pslf_form',
      'recommend_coaching_affordability_pslf',
    ],
  },
  'N-29': {
    rating: 3,
    description:
      'PSLF + NSLDS + IDR + PSLF Qualifying Count + No PSLF form + direct only, no PPLUS',
    components: [
      'score_need_adjustment',
      'summary_pslf_renew_idr',
      'recommend_pslf_tool',
      'transition_pslf_tool',
      'recommend_pslf_form_submission',
      'transition_pslf_form',
      'recommend_coaching_affordability_pslf',
    ],
  },
};

// Once the calculator ran after the user landed on the train, the generator swaps tool cards for result cards.
const TOOL_TYPES = new Set<ComponentType>(['to_strategy_finder', 'to_pslf_tool', 'to_idr_tool']);
const TOOL_RESULT_TYPES = new Set<ComponentType>([
  'transitional_strategy_finder_results',
  'transitional_pslf_tool_results',
  'transitional_idr_results',
]);

// The requesting_app fetch_last_recommendation reads for each calc's calculator.
const CALCULATOR_APP = {
  for_profit: 'IDR',
  non_profit_pslf: 'PSLF',
  non_profit_sf: 'STRATEGY_FINDER',
} as const;

// NSLDS loan_repayment_plan_idr_plan_anniversary_date on the demo IBR loan.
const DEMO_IDR_ANNIVERSARY_DATE = '2027-02-01';

const DAY_MS = 86_400_000;

// orjson's form of the generator's naive datetimes.
const pythonTimestamp = (value: Date | string) => new Date(value).toISOString().slice(0, 19);

const later = (a: string | null, b: string | null) => (a && b ? (a > b ? a : b) : (a ?? b));

// age_of_loans and been_paying_for_years: max(round(days / 365.25), 1) as 'N years'.
function yearsSince(isoDate: string, now: Date) {
  const days = Math.floor((now.getTime() - Date.parse(`${isoDate}T00:00:00Z`)) / DAY_MS);
  const years = Math.max(Math.round(days / 365.25), 1);
  return years === 1 ? '1 year' : `${years} years`;
}

// dateutil relativedelta(months=n) before now, clamped to the target month's last day.
function monthsBefore(now: Date, months: number) {
  const month = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - months, 1));
  const lastDay = new Date(
    Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0)
  ).getUTCDate();
  month.setUTCDate(Math.min(now.getUTCDate(), lastDay));
  month.setUTCHours(
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds(),
    now.getUTCMilliseconds()
  );
  return month.getTime();
}

// loan_cohorts.py; a boundary date falls into the later cohort.
function firstLoanTime(loanDate: string) {
  if (loanDate < '2007-10-01') return 'old_borrower';
  if (loanDate < '2014-07-01') return 'old_ibr_cohort';
  return loanDate < '2026-07-01' ? 'new_ibr_cohort' : 'obbb_cohort';
}

const lastLoanTime = (loanDate: string) =>
  loanDate < '2026-07-01' ? 'pre_obbb_cohort' : 'obbb_cohort';

// str.format over the generator's calculation placeholders.
const fill = (text: string, values: Record<string, string>) =>
  text.replace(/\{(\w+)\}/g, (_match, key: string) => values[key] ?? '__UNKNOWN__');

const hasDemoCopy = (code: WellnessTrainCode): code is DemoTrainCode => code in TRAINS;

// The generator's component loop: result cards replace tool cards once transitional, and the form-status card needs a submission within 11 months.
export function wellnessTrainComponents(
  code: DemoTrainCode,
  transitional: boolean,
  within11m: boolean,
  calculations: Record<string, string>
): WellnessComponent[] {
  return TRAINS[code].components.flatMap((name) => {
    const { type, text, button } = COMPONENTS[name];
    if (transitional ? TOOL_TYPES.has(type) : TOOL_RESULT_TYPES.has(type)) return [];
    if (type === 'transitional_pslf_form_submission' && !within11m) return [];
    return [
      {
        type,
        name,
        sub_title: COMPONENT_SUBTITLES[type] ?? null,
        text: fill(text, calculations),
        button: button && { ...button },
      },
    ];
  });
}

export interface WellnessPslfIndicators {
  countShown: boolean;
  submittedWithin11m: boolean;
  formSubmitted: boolean;
}

// train_name() plus its D-2 → D-5 step, for the demo's direct-only borrower in repayment with no default or forbearance.
export function wellnessTrainCode(
  calc: WellnessCalc | null,
  hasLinkedLoans: boolean,
  plan: NsldsPlanCode,
  pslf: WellnessPslfIndicators
) {
  if (calc === null) return 'D-5';
  if (!hasLinkedLoans) return 'D-2';
  switch (calc) {
    case 'for_profit':
      return plan === 'SF' ? 'N-3' : 'N-5';
    case 'non_profit_sf':
      if (plan === 'SF') return 'N-10';
      return pslf.countShown ? 'N-12' : 'N-13';
    case 'non_profit_pslf':
      if (plan === 'SF') return 'N-20';
      if (!pslf.countShown) return pslf.submittedWithin11m ? 'N-24' : 'N-23';
      if (pslf.submittedWithin11m) return 'N-22';
      return pslf.formSubmitted ? 'N-23' : 'N-29';
  }
}

// Mirrors the company-settings gates so every recommendation's route is open to the session.
export function wellnessCalcFor(
  scenario: DemoScenario,
  identity: DemoFixtureIdentity
): WellnessCalc {
  if (scenario.products.some((product) => product.kind === 'pslf')) {
    return 'non_profit_pslf';
  }
  return isPslfEligibleClient(identity.clientId) ? 'non_profit_sf' : 'for_profit';
}

// What get_and_set_wellness_train returns for the demo borrower now; null before a first-login user's first calculation.
export function buildWellnessTrain(
  selection: DemoSessionSelectionSnapshot,
  calc: WellnessCalc | null,
  now = new Date()
): WellnessTrainPayload | null {
  if (isFirstLoginPersona(selection.userScenarioId)) {
    return null;
  }
  const debt = getDemoDebtLevel(selection.debtLevelId);
  const linked = debt.hasLinkedLoans;
  const pslfActive = demoPslfActive(selection);
  const plan: NsldsPlanCode =
    demoLoanPlan(debt.repaymentPlan, pslfActive).planKey === DEMO_PSLF_LOAN_PLAN.planKey
      ? 'IB'
      : 'SF';
  const pslfCount = pslfActive ? DEMO_PSLF_QUALIFYING_PAYMENTS : 0;

  const nsldsDoc = linked
    ? DEMO_DOCUMENTS.find((doc) => doc.provider === 'ObservableProvider.NSLDS')
    : undefined;
  const statementDoc = linked
    ? DEMO_DOCUMENTS.find((doc) => doc.provider === 'ObservableProvider.ACCOUNT_STATEMENT')
    : undefined;
  const nsldsAt = nsldsDoc ? pythonTimestamp(nsldsDoc.createdAt) : null;
  const statementBalanceAt = linked ? `${DEMO_LOAN_BALANCE_AS_OF.slice(0, 10)}T00:00:00` : null;
  const statementObsAt = linked ? pythonTimestamp(DEMO_LOAN_BALANCE_AS_OF) : null;
  const balanceUpdatedAt = later(statementBalanceAt, nsldsAt);

  const submittedDate =
    calc === 'non_profit_pslf' ? demoPslfLatestSubmittedAt(selection.pslfScenarioId) : null;
  const submittedAt = submittedDate ? Date.parse(`${submittedDate}T00:00:00Z`) : null;
  const within11m = submittedAt !== null && submittedAt > monthsBefore(now, 11);
  const within4m = submittedAt !== null && submittedAt > monthsBefore(now, 4);

  const lastRun = calc ? demoLatestLegacyRun(selection, CALCULATOR_APP[calc]) : null;
  const lastRunAt = lastRun ? pythonTimestamp(lastRun.createdAt) : null;
  // The generator's value for a user with no earlier train history: the balance date, else None.
  const firstTimeOnThisTrain = balanceUpdatedAt;
  const transitional =
    lastRunAt !== null && firstTimeOnThisTrain !== null && firstTimeOnThisTrain < lastRunAt;

  const code = wellnessTrainCode(calc, linked, plan, {
    countShown: pslfCount > 0,
    submittedWithin11m: within11m,
    formSubmitted: submittedDate !== null,
  });
  if (!hasDemoCopy(code)) {
    throw new Error(`[demo] copy wellness train ${code} from wellness_train_config.py first`);
  }
  const train = TRAINS[code];
  // A consolidation loan enters repayment status on its loan date.
  const beenPayingForYears = nsldsDoc ? yearsSince(DEMO_LOAN_ORIGINAL_DATE, now) : '0 years';
  const calculations = {
    age_of_loans: linked ? yearsSince(DEMO_LOAN_ORIGINAL_DATE, now) : 'None',
    years_paying: beenPayingForYears,
    pslf_count: nsldsAt ? String(pslfCount) : '__UNKNOWN__',
    pslf_remaining_count: nsldsAt ? String(120 - pslfCount) : '__UNKNOWN__',
  };
  const components = wellnessTrainComponents(code, transitional, within11m, calculations);

  return {
    wellness_train: code,
    wellness_train_rating: train.rating,
    wellness_train_description: train.description,
    components,
    has_linked_loans: linked,
    balance_updated_at: balanceUpdatedAt,
    evidence_of_loans: linked,
    obs_updated_at: later(statementObsAt, nsldsAt),
    calculation_updated_at: pythonTimestamp(now),
    nslds: nsldsDoc !== undefined,
    pslf_form_submitted_date: submittedDate,
    pslf_form_submitted: submittedDate !== null,
    pslf_submitted_within_11m: within11m,
    pslf_submitted_within_4m: within4m,
    been_paying_for_years: beenPayingForYears,
    has_used_calculator: lastRun !== null,
    calculator_last_use_record_id: lastRun?.id ?? null,
    calculator_last_use_date: lastRunAt,
    latest_nslds_doc_id: nsldsDoc?.id ?? null,
    latest_loan_statement_doc_id: statementDoc?.id ?? null,
    ...(nsldsAt
      ? {
          nslds_as_of: nsldsAt,
          nslds_processed_as_of: nsldsAt,
          has_ffel_loans: false,
          has_direct_loans: true,
          has_perkins_loans: false,
          has_parentplus_loans: false,
          in_grace_period_or_deferment: false,
          non_optimal_repayment_plan: plan === 'SF',
          idr_repayment_plan: plan === 'IB',
          pslf_count_shown: pslfCount > 0,
          pslf_count: pslfCount,
          idr_anniversary_date: plan === 'IB' ? DEMO_IDR_ANNIVERSARY_DATE : null,
          balance_going_down: true,
          has_recent_forbearance: false,
          has_default_history: false,
          currently_in_default: false,
          currently_in_forbearance: false,
          first_loan_date: DEMO_LOAN_ORIGINAL_DATE,
          last_loan_date: DEMO_LOAN_ORIGINAL_DATE,
          first_loan_time: firstLoanTime(DEMO_LOAN_ORIGINAL_DATE),
          last_loan_time: lastLoanTime(DEMO_LOAN_ORIGINAL_DATE),
        }
      : {
          past_due_payments: false,
          has_federal_loans: linked,
          grace_or_forbearance: !linked,
        }),
    ...(calc ? { file_type: nsldsAt ? ('nslds' as const) : ('statement' as const) } : {}),
    calc,
    version: nsldsAt ? `${WELLNESS_TRAIN_VERSION}.nslds` : WELLNESS_TRAIN_VERSION,
    statement_balance_updated_at: statementBalanceAt,
    statement_obs_updated_at: statementObsAt,
    first_time_on_this_train: firstTimeOnThisTrain,
  };
}

export function buildWellnessMatchers(
  selection: DemoSessionSelectionSnapshot,
  calc: WellnessCalc | null
): MatcherFixture[] {
  return [
    {
      matcher: { kind: 'builder', method: 'GET', urlIncludes: WELLNESS_PROFILE_URL },
      contentFor: () => buildWellnessTrain(selection, calc),
    },
  ];
}

// Registered after the products, so a session without Financial Wellness gets the no-access train.
export function buildWellnessCatchAllMatchers(
  selection: DemoSessionSelectionSnapshot
): MatcherFixture[] {
  return [
    ...buildWellnessMatchers(selection, null),
    {
      matcher: { kind: 'builder', method: 'POST', urlIncludes: WELLNESS_ENQUEUE_URL },
      content: null,
    },
  ];
}

const ANSWER_PROFILE_URL_RE = /\/answer-profiles\/(demo-[a-z0-9-]+)(?:\?|$)/;

const postedUserId = (body: unknown) =>
  (body as { data?: { relationships?: { user?: { data?: { id?: unknown } | null } } } } | null)
    ?.data?.relationships?.user?.data?.id;

// The decline-account modal saves the demo answer profile.
export function buildWellnessFetchMatchers(): FetchMatcher[] {
  return [
    {
      urlMatches: (url) => ANSWER_PROFILE_URL_RE.test(url),
      method: 'PATCH',
      bodyMatches: (body) => {
        const userId = postedUserId(body);
        return userId === undefined || userId === USER_ID;
      },
      // Identifier only, so the saved answers survive the commit.
      json: (url: string) => ({
        data: { type: 'answer-profile', id: ANSWER_PROFILE_URL_RE.exec(url)?.[1] ?? '' },
      }),
    },
  ];
}
